import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Palette, Image as ImageIcon, Upload, Trash2, Save, Loader2, Check,
  Instagram, Facebook, Twitter, Hash, Globe, Quote, Droplet,
} from "lucide-react";
import { toast } from "sonner";

import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";

function authedAssetUrl(path) {
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return `${base}${path}?t=${Date.now()}`; // bust cache after upload
}

const FIELD_LABEL = "text-[11px] font-mono uppercase tracking-wider text-ink-muted";
const INPUT_CLS = "w-full rounded-md border border-line bg-bg-elevated text-ink-primary px-3 py-2 text-sm placeholder:text-ink-muted focus:border-brand-amber focus:outline-none";

function ColorRow({ label, value, onChange, testid }) {
  const safe = value && /^#/.test(value) ? value : "#000000";
  return (
    <div>
      <label className={FIELD_LABEL}>{label}</label>
      <div className="mt-1 flex items-center gap-2">
        <input
          type="color"
          value={safe}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`${testid}-picker`}
          className="h-9 w-9 rounded-md border border-line bg-bg-elevated p-0.5 cursor-pointer"
        />
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#FF8A1F"
          data-testid={`${testid}-hex`}
          className={`${INPUT_CLS} font-mono`}
        />
      </div>
    </div>
  );
}

function AssetUploader({ kind, label, current, onUpload, onClear, busy }) {
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Image only"); return; }
    if (f.size > 4 * 1024 * 1024) { toast.error("Max 4 MB"); return; }
    onUpload(f);
    e.target.value = "";
  };
  return (
    <div className="rounded-lg border border-line bg-bg-elevated p-3" data-testid={`${kind}-uploader`}>
      <label className={`${FIELD_LABEL} block mb-2`}>{label}</label>
      {current ? (
        <div className="flex items-center gap-3">
          <img src={current} alt={label} className="h-16 w-16 rounded object-contain bg-bg shrink-0 ring-1 ring-line" data-testid={`${kind}-preview`} />
          <div className="flex-1 min-w-0">
            <div className="text-xs text-emerald-300 flex items-center gap-1"><Check className="h-3 w-3" /> uploaded</div>
            <div className="mt-1 flex gap-2">
              <label className="text-[11px] font-mono px-2 py-1 rounded border border-line bg-bg-surface cursor-pointer hover:border-brand-amber/40" data-testid={`${kind}-replace`}>
                Replace
                <input type="file" accept="image/*" className="hidden" onChange={onFile} />
              </label>
              <button onClick={onClear} disabled={busy} className="text-[11px] font-mono px-2 py-1 rounded border border-brand-onair/30 text-brand-onair hover:bg-brand-onair/5" data-testid={`${kind}-clear`}>
                <Trash2 className="h-3 w-3 inline" /> Clear
              </button>
            </div>
          </div>
        </div>
      ) : (
        <label className="block rounded border-2 border-dashed border-line p-4 text-center cursor-pointer hover:border-brand-amber/40 transition" data-testid={`${kind}-dropzone`}>
          <Upload className="h-6 w-6 text-ink-muted mx-auto mb-1" />
          <div className="text-xs">Upload {label.toLowerCase()}</div>
          <div className="text-[10px] text-ink-muted mt-0.5">PNG with transparency works best · up to 4 MB</div>
          <input type="file" accept="image/*" className="hidden" onChange={onFile} />
        </label>
      )}
    </div>
  );
}

export default function BrandKit() {
  const [kit, setKit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // form state
  const [primary, setPrimary] = useState("");
  const [secondary, setSecondary] = useState("");
  const [accent, setAccent] = useState("");
  const [slogan, setSlogan] = useState("");
  const [website, setWebsite] = useState("");
  const [instagram, setInstagram] = useState("");
  const [facebook, setFacebook] = useState("");
  const [twitter, setTwitter] = useState("");
  const [tiktok, setTiktok] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/creative-studio/brand-kit");
      setKit(r.data);
      setPrimary(r.data.primary_color || "");
      setSecondary(r.data.secondary_color || "");
      setAccent(r.data.accent_color || "");
      setSlogan(r.data.slogan || "");
      setWebsite(r.data.website || "");
      const s = r.data.social || {};
      setInstagram(s.instagram || ""); setFacebook(s.facebook || "");
      setTwitter(s.twitter || "");     setTiktok(s.tiktok || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load brand kit");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("primary_color", primary);
      fd.append("secondary_color", secondary);
      fd.append("accent_color", accent);
      fd.append("slogan", slogan);
      fd.append("website", website);
      fd.append("instagram", instagram);
      fd.append("facebook", facebook);
      fd.append("twitter", twitter);
      fd.append("tiktok", tiktok);
      const r = await api.put("/creative-studio/brand-kit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setKit(r.data);
      toast.success("Brand kit saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const uploadAsset = async (kind, file) => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append(kind, file);
      const r = await api.put("/creative-studio/brand-kit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setKit(r.data);
      toast.success(`${kind === "logo" ? "Logo" : "Watermark"} uploaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setSaving(false); }
  };

  const clearAsset = async (kind) => {
    setSaving(true);
    try {
      await api.delete(`/creative-studio/brand-kit/asset/${kind}`);
      await load();
      toast.success("Removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-10 text-center font-mono text-xs text-ink-muted">Loading brand kit…</div>;
  if (!kit) return null;

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto animate-fade-up" data-testid="brand-kit-page">
      <Link to="/dashboard/creative-studio" className="inline-flex items-center gap-1 text-xs text-ink-muted hover:text-brand-amber transition mb-4">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Creative Studio
      </Link>

      <PageHeader
        eyebrow="// BRAND KIT"
        title="Your station's visual identity."
        description="Set your colors, slogan, socials, logo and watermark once — toggle 'Apply brand' in Creative Studio to bias every generation toward your identity. The watermark is composited pixel-perfectly after generation."
      />

      {/* Assets */}
      <div className="grid md:grid-cols-2 gap-3 mb-6">
        <AssetUploader
          kind="logo" label="Logo"
          current={kit.logo_configured ? authedAssetUrl(kit.logo_url) : ""}
          onUpload={(f) => uploadAsset("logo", f)}
          onClear={() => clearAsset("logo")}
          busy={saving}
        />
        <AssetUploader
          kind="watermark" label="Watermark"
          current={kit.watermark_configured ? authedAssetUrl(kit.watermark_url) : ""}
          onUpload={(f) => uploadAsset("watermark", f)}
          onClear={() => clearAsset("watermark")}
          busy={saving}
        />
      </div>

      {/* Colors */}
      <div className="rounded-xl border border-line bg-bg-surface p-5 mb-6" data-testid="colors-section">
        <h3 className="font-display text-lg font-bold mb-3 flex items-center gap-2">
          <Droplet className="h-5 w-5 text-brand-amber" /> Brand colors
        </h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <ColorRow label="Primary"   value={primary}   onChange={setPrimary}   testid="primary" />
          <ColorRow label="Secondary" value={secondary} onChange={setSecondary} testid="secondary" />
          <ColorRow label="Accent"    value={accent}    onChange={setAccent}    testid="accent" />
        </div>
      </div>

      {/* Voice + web */}
      <div className="rounded-xl border border-line bg-bg-surface p-5 mb-6" data-testid="voice-section">
        <h3 className="font-display text-lg font-bold mb-3 flex items-center gap-2">
          <Quote className="h-5 w-5 text-brand-blue" /> Voice & web
        </h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className={FIELD_LABEL}>Slogan</label>
            <input value={slogan} onChange={(e) => setSlogan(e.target.value)} placeholder="e.g. Live where it happens" data-testid="slogan-input" className={`mt-1 ${INPUT_CLS}`} />
          </div>
          <div>
            <label className={FIELD_LABEL}>Website</label>
            <div className="mt-1 relative">
              <Globe className="h-4 w-4 text-ink-muted absolute left-2 top-1/2 -translate-y-1/2" />
              <input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://yourstation.com" data-testid="website-input" className={`${INPUT_CLS} pl-7`} />
            </div>
          </div>
        </div>
      </div>

      {/* Socials */}
      <div className="rounded-xl border border-line bg-bg-surface p-5 mb-6" data-testid="socials-section">
        <h3 className="font-display text-lg font-bold mb-3 flex items-center gap-2">
          <Hash className="h-5 w-5 text-cyan-300" /> Social handles
        </h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            ["Instagram", instagram, setInstagram, Instagram, "@yourstation",  "instagram"],
            ["Facebook",  facebook,  setFacebook,  Facebook,  "facebook.com/yourstation", "facebook"],
            ["X / Twitter", twitter, setTwitter,   Twitter,   "@yourstation",  "twitter"],
            ["TikTok",    tiktok,    setTiktok,    Hash,      "@yourstation",  "tiktok"],
          ].map(([label, val, setter, Icon, ph, tid]) => (
            <div key={tid}>
              <label className={FIELD_LABEL}>{label}</label>
              <div className="mt-1 relative">
                <Icon className="h-4 w-4 text-ink-muted absolute left-2 top-1/2 -translate-y-1/2" />
                <input value={val} onChange={(e) => setter(e.target.value)} placeholder={ph} data-testid={`social-${tid}`} className={`${INPUT_CLS} pl-7`} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          data-testid="save-kit-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-brand-amber text-bg font-bold disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save brand kit
        </button>
      </div>
    </div>
  );
}
